import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Venue } from 'Src/models/venue';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { getPostcode, searchFunctionSchoolPostcodes } from 'Src/helpers/autocomplete/autocomplete';
import { Map } from 'Src/components/Map/Map';
import './VenuesForm.css';
import { POINT } from 'Src/consts/venue';
import { Switch } from 'Src/components/Switch/Switch';

interface Props {
  user: AppUser;
  venue?: Venue;
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const venueSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  postcode: Yup.string().required('Required'),
  postcodeId: Yup.string().required('Required'),
  isHome: Yup.boolean(),
  point: Yup.object().shape({
    type: Yup.string().required('Required'),
    coordinates: Yup.array()
  })
});

export function VenueForm(props: Props) {
  const { venue, user, onSubmit, onCancel } = props;
  const { activeSchool } = user;
  const { postcode } = activeSchool;

  function getPostcodes(text: string) {
    return searchFunctionSchoolPostcodes(user, text);
  }

  function onSubmitForm(values) {
    const { name, isHome, point, postcodeId } = values;

    onSubmit({
      name,
      isHome,
      postcodeId,
      point
    });
  }

  const venueInitial = {
    name: propz.get(venue, ['name'], ''),
    postcodeId: propz.get(venue, ['postcodeId'], postcode.id),
    postcode: propz.get(venue, ['postcode'], postcode.postcode),
    isHome: propz.get(venue, ['isHome'], false),
    point: {
      type: POINT,
      coordinates: propz.get(venue, ['point', 'coordinates'], postcode.point.coordinates)
    }
  };

  return (
    <Formik
        initialValues={venueInitial}
        validationSchema={venueSchema}
        onSubmit={values => {
          onSubmitForm(values);
        }}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <Form className="eVenueForm">
            <div className="eVenueForm__main">
                <div className="eVenueForm__field eVenueForm__field--name">
                  <label className="eVenueForm__label">Name</label>
                  <Field type="text" name="name" className="form-control" />
                  {touched.name && errors.name ? <div className="alert alert-danger mt-2">{errors.name}</div> : null}
                </div>

                <div className="eVenueForm__field eVenueForm__field--postcode">
                  <label className="eVenueForm__label">Postcode</label>

                  <Field name="postcode">
                    {({ field }) => {
                      return (
                        <Autocomplete
                          searchFunction={getPostcodes}
                          getElementTitle={getPostcode}
                          customClass="mFullWidth"
                          defaultItem={values}
                          onSelect={postcodeItem => {
                            setFieldValue('postcodeId', postcodeItem.id);
                            setFieldValue('postcode', postcodeItem.postcode);
                            setFieldValue('point', postcodeItem.point);
                          }}
                        />
                      );
                    }}
                  </Field>

                  {touched.postcode && errors.postcode ? (
                    <div className="alert alert-danger mt-2">{errors.postcode}</div>
                  ) : null}
                </div>

                <div className="eVenueForm__field eVenueForm__field--home">
                  <div className="eVenueForm__switchRow">
                    <Field name="isHome">
                      {({ field }) => (
                        <Switch
                          {...field}
                          value={values.isHome}
                          customClass="mb-0"
                          text="Mark as home venue"
                          onChange={event => {
                            const isHome = event.target.checked;
                            setFieldValue('isHome', isHome);
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </div>
                {values.postcode ? (
                  <div className="eVenueForm__mapPreview">
                    <Map
                      key={`${values.postcodeId}-${values.point.coordinates.join('-')}`}
                      point={values.point}
                      customStylingClass="eVenueForm__workingMap"
                      isMarkerDraggable={false}
                    />
                    <span className="eVenueForm__postcodeChip">⌖ {values.postcode}</span>
                  </div>
                ) : null}
            </div>

            <div className="eVenueForm__footer">
              <button type="button" onClick={onCancel} className="eVenueForm__btnSecondary">
                Cancel
              </button>
              <button type="submit" className="eVenueForm__btnPrimary">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Save venue
              </button>
            </div>
          </Form>
        )}
    </Formik>
  );
}
